/**
 * Module dependencies.
 */

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

/**
 * A single breadcrumb crumb — a label and, optionally, a path to link to.
 */

export type BreadcrumbCrumb = { label: string; pathname?: string };

/**
 * What a page publishes for the trailing portion of the breadcrumb: either a
 * single record crumb *appended* after the auto section crumb (e.g. "… > BTC"),
 * or an explicit list that *replaces* the auto section crumb (so a page can keep
 * the navigation it was reached through, e.g. "Wallets > My Wallet > BTC").
 */

type BreadcrumbTrail = { crumbs: BreadcrumbCrumb[]; replacesSection: boolean } | null;

/**
 * `BreadcrumbContext` type.
 */

type BreadcrumbContextValue = {
  trail: BreadcrumbTrail;
  setTrail: (value: BreadcrumbTrail) => void;
};

/**
 * Breadcrumb context — lets a page publish trailing crumb(s) (e.g. the current
 * record's name, or the path it was reached through) so the shared `Breadcrumb`
 * can append or override the auto section crumb.
 */

const BreadcrumbContext = createContext<BreadcrumbContextValue>({
  trail: null,
  setTrail: () => undefined
});

/**
 * Export `BreadcrumbProvider` component.
 */

export function BreadcrumbProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [trail, setTrail] = useState<BreadcrumbTrail>(null);
  const value = useMemo(() => ({ trail, setTrail }), [trail]);

  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>;
}

/**
 * Export `useBreadcrumb` — read the current trailing crumb(s).
 */

export function useBreadcrumb(): BreadcrumbContextValue {
  return useContext(BreadcrumbContext);
}

/**
 * Export `useSetBreadcrumbTrail` — publish the trailing portion of the breadcrumb
 * for the current page (cleared automatically on unmount / when it changes).
 *
 * Pass a string for the common case (a single record crumb appended after the
 * auto section crumb, e.g. "… > Conta Refeição"), or an array of crumbs to fully
 * replace the auto section crumb — useful when a detail page must preserve the
 * navigation it was reached through (e.g. "Wallets > My Wallet > BTC").
 */

export function useSetBreadcrumbTrail(value?: string | BreadcrumbCrumb[] | null): void {
  const { setTrail } = useBreadcrumb();

  // A stable key so an inline array/string doesn't re-fire the effect every
  // render (the identity changes, the content usually doesn't).
  const key = Array.isArray(value)
    ? value.map(crumb => `${crumb.label}|${crumb.pathname ?? ''}`).join('>')
    : value ?? '';

  useEffect(() => {
    if (Array.isArray(value)) {
      setTrail(value.length ? { crumbs: value, replacesSection: true } : null);
    } else if (value && value.trim()) {
      setTrail({ crumbs: [{ label: value }], replacesSection: false });
    } else {
      setTrail(null);
    }

    return () => setTrail(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, setTrail]);
}
